import { useEffect, useMemo, useRef } from "react";
import { LocResources } from "./locResources";
import Helper from "./Helper";
import { EventHub, IEventHub } from "./eventHub";
import {
    buildRdsCallbackUrl,
    RdsCallbackValidationFailure,
} from "./rdsRedirection";
import "./RdsConsentPage.css";

const locResources = new LocResources(Helper.getLanguageParam());

export interface IRdsConsentPageProps {
    email: string;
    env: string;
    greenId: string;
    sessionId: string;
    telemetry?: IEventHub;
}

export const RdsConsentTelemetryEvents = {
    requestReceived: "RdsConsent_Request_Received",
    invalidRequest: "RdsConsent_Invalid_Request",
    submit: "RdsConsent_Submit",
} as const;

export type RdsConsentValidationFailure =
    | "MissingGreenId"
    | RdsCallbackValidationFailure;

type RdsConsentValidationResult =
    | { actionUrl: string; rdsSessionId: string; failureReason?: never }
    | {
        actionUrl: null;
        rdsSessionId: string | null;
        failureReason: RdsConsentValidationFailure;
    };

export function validateRdsConsentRequest(
    sessionId: string,
    greenId: string,
    env: string
): RdsConsentValidationResult {
    if (!greenId.trim()) {
        return {
            actionUrl: null,
            rdsSessionId: null,
            failureReason: "MissingGreenId",
        };
    }

    const callbackResult = buildRdsCallbackUrl(sessionId, env);
    if (callbackResult.callbackUrl === null) {
        return {
            actionUrl: null,
            rdsSessionId: null,
            failureReason: callbackResult.failureReason,
        };
    }

    return {
        actionUrl: callbackResult.callbackUrl,
        rdsSessionId: callbackResult.rdsSessionId,
    };
}

function RdsConsentPage(props: IRdsConsentPageProps) {
    const submissionTrackedRef = useRef(false);
    const telemetry = useMemo(
        () => props.telemetry || new EventHub({
            pageContext: { pageName: "RdsConsent" },
            partnerContext: { partnerId: "rds", env: props.env },
        }),
        [props.env, props.telemetry]
    );
    const validationResult = validateRdsConsentRequest(
        props.sessionId,
        props.greenId,
        props.env
    );
    const canSubmit = validationResult.actionUrl !== null;
    const telemetryProperties = {
        environment: props.env,
        greenId: props.greenId,
        rdsSessionId: validationResult.rdsSessionId,
    };

    useEffect(() => {
        telemetry.trackEvent(
            RdsConsentTelemetryEvents.requestReceived,
            telemetryProperties
        );

        if (!canSubmit) {
            telemetry.trackEvent(RdsConsentTelemetryEvents.invalidRequest, {
                ...telemetryProperties,
                reason: validationResult.failureReason,
            });
            telemetry.flush();
        }
    }, [
        canSubmit,
        telemetry,
        props.env,
        props.greenId,
        validationResult.failureReason,
        validationResult.rdsSessionId,
    ]);

    const trackSubmission = (action: "confirm" | "cancel") => {
        if (!submissionTrackedRef.current) {
            submissionTrackedRef.current = true;
            telemetry.trackEvent(RdsConsentTelemetryEvents.submit, {
                ...telemetryProperties,
                action,
            });
            // Native form submission unloads the page, so send queued events first.
            telemetry.flush();
        }
    };

    return (
        <main className="rds-consent-page">
            <section
                className="rds-consent-card"
                aria-labelledby="rds-consent-title"
            >
                <h1 id="rds-consent-title">{locResources.getByName("RdsConsentTitle")}</h1>
                <p className="rds-consent-lead">{locResources.getByName("RdsConsentLead")}</p>

                <div
                    className="rds-consent-summary"
                    role="group"
                    aria-label={locResources.getByName("RdsConsentAccountDetailsLabel")}
                >
                    <span className="rds-consent-label">
                        {locResources.getByName("RdsConsentMicrosoftAccountLabel")}
                    </span>
                    <span className="rds-consent-value">{props.email}</span>
                </div>

                <p className="rds-consent-warning">{locResources.getByName("RdsConsentWarning")}</p>

                {!canSubmit && (
                    <div className="rds-consent-status error" role="alert">
                        {locResources.getByName("RdsConsentRequestError")}
                    </div>
                )}

                <form action={validationResult.actionUrl || undefined} method="post">
                    <input type="hidden" name="greenId" value={props.greenId} />
                    <div className="rds-consent-actions">
                        <button
                            className="rds-consent-button secondary"
                            type="submit"
                            name="action"
                            value="cancel"
                            disabled={!canSubmit}
                            onClick={() => trackSubmission("cancel")}
                        >
                            {locResources.getByName("RdsConsentCancel")}
                        </button>
                        <button
                            className="rds-consent-button primary"
                            type="submit"
                            name="action"
                            value="confirm"
                            disabled={!canSubmit}
                            onClick={() => trackSubmission("confirm")}
                        >
                            {locResources.getByName("RdsConsentConfirm")}
                        </button>
                    </div>
                </form>
            </section>
        </main>
    );
}

export default RdsConsentPage;
